import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Trash2, ChevronLeft, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function DataDeletion() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/data-deletion", { email, reason });
      toast.success("Deletion request received");
      setDone(true);
      // Cache the confirmation code for Meta callback (URL param support)
      const params = new URLSearchParams(window.location.search);
      if (params.get("id")) {
        window.history.replaceState({}, "", `/data-deletion?id=${params.get("id")}&status=received&code=${r.data.confirmation_code}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit request");
    } finally { setBusy(false); }
  };

  const params = new URLSearchParams(window.location.search);
  const metaId = params.get("id"); // Meta sends signed_request but the resulting page must show a confirmation code

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white">
      <SEO title="Data Deletion · Nexus" description="Request full deletion of your Nexus account and associated data." path="/data-deletion" />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-8"><ChevronLeft className="h-3 w-3" /> Back</Link>
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="h-6 w-6 text-nexus-pink" />
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Data deletion</div>
        </div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">Delete everything.</h1>
        <p className="text-zinc-400 mt-3 text-sm leading-relaxed">Under UK GDPR you can request permanent deletion of your Nexus account and all associated data at any time. Submissions are actioned within 30 days.</p>
        <div className="mt-5 p-4 rounded-xl bg-white/[0.03] border border-white/10 text-sm text-zinc-300 leading-relaxed" data-testid="social-data-deletion-note">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Data from Instagram, TikTok or Google</div>
          Data we obtained from Instagram, TikTok or Google can be removed at any time from <a href="/settings" className="text-nexus-blue hover:text-white">Settings → Connections → Disconnect</a> (immediate), or by deleting your account below (completed within 30 days). Google users can also revoke access at <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="text-nexus-blue hover:text-white">myaccount.google.com/permissions</a>.
        </div>

        {metaId && !done && (
          <div className="mt-6 p-4 rounded-xl border border-nexus-blue/40 bg-nexus-blue/10 text-xs text-zinc-200 flex items-start gap-2" data-testid="meta-callback-banner">
            <AlertTriangle className="h-4 w-4 text-nexus-blue mt-0.5 shrink-0" />
            <div>You were sent here from Facebook/Instagram to remove your Nexus data. Enter the email you signed up with and we&apos;ll handle the rest.</div>
          </div>
        )}

        {done ? (
          <div className="mt-10 glass rounded-2xl p-8 text-center" data-testid="deletion-confirmation">
            <div className="font-display font-bold text-2xl">Request received.</div>
            <p className="text-sm text-zinc-400 mt-2">We&apos;ll email you within 24 hours to confirm and complete the deletion within 30 days.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 glass rounded-2xl p-6 space-y-4" data-testid="deletion-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">Your account email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="you@example.com" className="bg-white/[0.02] border-white/10 text-white" data-testid="deletion-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">Reason <span className="normal-case tracking-normal text-[10px] text-zinc-500">(optional)</span></label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Anything we could have done better?" className="w-full bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm p-3 focus:outline-none focus:border-white/25" data-testid="deletion-reason" />
            </div>
            <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20 text-xs text-zinc-300 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" />
              <div>This is <strong>permanent</strong>. Your profile, campaigns, hires, messages, portfolio, growth-AI reports, referral rewards and payment history will all be removed. Financial records legally required for tax may be retained for 7 years but anonymised.</div>
            </div>
            <Button type="submit" disabled={busy} className="w-full nexus-gradient text-white rounded-full font-semibold border-0 hover:opacity-90" data-testid="deletion-submit">
              {busy ? "Submitting…" : "Request permanent deletion"}
            </Button>
          </form>
        )}

        <p className="text-xs text-zinc-500 mt-6">
          Prefer email? Write to <a href="mailto:info@collabnexus.co.uk" className="text-nexus-blue hover:text-white">info@collabnexus.co.uk</a> with subject &ldquo;Delete my Nexus account&rdquo;.
        </p>
      </div>
    </div>
  );
}
